/**
 * Per-company feature modules — one per top-level sidebar group.
 *
 * Single source of truth shared by the sidebar lock UI and the ModuleGuard
 * route guard. Codes mirror the backend list in
 * backend/domain/company_feature/constants/constants.go — keep in sync.
 */

export const MODULE_CODES = {
  MGMT_DASHBOARD: 'mgmt_dashboard',
  MANIFEST: 'manifest',
  KEPABEANAN: 'kepabeanan',
  KUOTA_IMPOR_EKSPOR: 'kuota_impor_ekspor',
  CUKAI: 'cukai',
  CUSTOMER_APPROVAL: 'customer_approval',
  CREDIT_TOPUP: 'credit_topup',
  LANGGANAN: 'langganan',
  BERITA: 'berita',
  LAYANAN_LAINNYA: 'layanan_lainnya',
  MASTER: 'master',
} as const;

export type ModuleCode = (typeof MODULE_CODES)[keyof typeof MODULE_CODES];

export interface ModuleDef {
  code: ModuleCode;
  /** Sidebar top-level label this module corresponds to. */
  sidebarLabel: string;
  /** Route path prefixes owned by the module (matched on segment boundaries). */
  routePrefixes: string[];
}

export const MODULES: ModuleDef[] = [
  {
    code: MODULE_CODES.MGMT_DASHBOARD,
    sidebarLabel: 'Mgmt Dashboard',
    routePrefixes: ['/management-dashboard'],
  },
  {
    code: MODULE_CODES.MANIFEST,
    sidebarLabel: 'Manifest',
    routePrefixes: ['/manifest', '/draft-manifest'],
  },
  {
    code: MODULE_CODES.KEPABEANAN,
    sidebarLabel: 'Kepabeanan',
    routePrefixes: [
      '/dashboard',
      '/buat-aju',
      '/doc-pabean',
      '/monitoring-blokir',
      '/respon',
      '/database-barang',
      '/ppftz',
      '/bc23',
      '/export',
    ],
  },
  {
    code: MODULE_CODES.KUOTA_IMPOR_EKSPOR,
    sidebarLabel: 'Kuota Impor / Ekspor',
    routePrefixes: ['/shipment-quota', '/harga-referensi'],
  },
  {
    code: MODULE_CODES.CUKAI,
    sidebarLabel: 'Cukai',
    routePrefixes: ['/cukai'],
  },
  {
    code: MODULE_CODES.CUSTOMER_APPROVAL,
    sidebarLabel: 'Customer Approval',
    routePrefixes: ['/customer-approval'],
  },
  {
    code: MODULE_CODES.CREDIT_TOPUP,
    sidebarLabel: 'Credit Topup',
    routePrefixes: ['/credit'],
  },
  {
    code: MODULE_CODES.LANGGANAN,
    sidebarLabel: 'Langganan',
    routePrefixes: ['/subscription'],
  },
  {
    code: MODULE_CODES.BERITA,
    sidebarLabel: 'Berita',
    routePrefixes: ['/berita'],
  },
  {
    code: MODULE_CODES.LAYANAN_LAINNYA,
    sidebarLabel: 'Layanan Lainnya',
    // /pengaturan is deliberately absent: personal settings are where a user
    // turns things off, so the page stays reachable whatever modules a company
    // holds. See src/app/(dashboard)/pengaturan/page.tsx.
    routePrefixes: ['/stamp-duty', '/mini-games', '/file-compression', '/split-pdf'],
  },
  {
    code: MODULE_CODES.MASTER,
    sidebarLabel: 'Master',
    routePrefixes: ['/master'],
  },
];

export const MODULE_BY_SIDEBAR_LABEL: Record<string, ModuleCode> = Object.fromEntries(
  MODULES.map((m) => [m.sidebarLabel, m.code])
);

/** Matches /prefix and /prefix/... but not /prefix-something-else. */
function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** The module owning a pathname, or undefined for ungated routes. */
export function moduleForPath(pathname: string): ModuleDef | undefined {
  return MODULES.find((m) => m.routePrefixes.some((p) => matchesPrefix(pathname, p)));
}
